import React, { useState, useEffect, useMemo } from "react";
import { ChartExtraBar, formartChartText, initSzieForScale, TextBox, Title, validateData } from "./common";
import ViewTestTab from "./viewTestTab";
import { ChartsType, horizontalBarType, ICharts, ISoftData, KindDataType, ShockType } from "@Types/kindSumReport";
import { fetchSuperTestCharts, fetchSuperTestShock } from "@Services/kindsum";
import { ObserverWrapper } from "@Components/article/superTest/observerLayout";
import BarGroup from "./chart/barGroup";
import LineChart from "./chart/line";
import FixedTable from "./fixedTable";
import { useQuery } from "react-query";

const defaultSoftData: ISoftData = {
  chartData: {},
  compareMyid: 0,
  kindData: {} as KindDataType,
  myids: 0,
  rankData: [],
};

interface IProps {
  id: number;
  comfortModelYearId: number;
  // data: ISoftData;
  // shockData: ShockType[];
}

interface IChartsProps {
  res: ICharts | false;
  kindData: KindDataType;
}

interface ISpeedChartsProps {
  res: ICharts | false;
  shockData: ShockType[];
}

// 車室隔音對照表
const CarSoundproofingDom: React.FC<IChartsProps> = ({ res, kindData }) => {
  const [upKeys, setUpKeys] = useState<string[]>([]);

  if (res) {
    const { kindInfo } = kindData;
    const titles = [["product"].concat(res.chartData.map((item) => item.title))];
    const dataSource = (res.chartData as any[]).map((item) => [
      item.title,
      ...item.value.map((item: string) => Number(item)),
    ]);
    const extraKeys = (res.chartData as horizontalBarType[])[0]?.key;

    const extraDataArr = extraKeys
      .map((item, inx) => {
        const data = kindInfo.filter((i) => i.myid === item)?.[0];

        return data ? { name: data.title, value: data.environment, color: "#3264FF" } : false;
      })
      .filter((item) => item !== false);

    return (
      <div className={"supertest-observer"}>
        <ObserverWrapper
          name='sound-proofing-chart'
          observerOnceCallBack={(key) => setUpKeys((prev) => (!prev.includes(key) ? prev.concat(key) : prev))}
        >
          <BarGroup
            id='sound-proofing-chart'
            width={initSzieForScale(270)}
            height={initSzieForScale(170)}
            dataSource={titles.concat(dataSource as any)}
            visibleKeys={upKeys}
          />
        </ObserverWrapper>
        <ChartExtraBar dataSource={extraDataArr} />
      </div>
    );
  }

  return <></>;
};

// 30km/h震動曲線表現
const CarShakeLine30: React.FC<ISpeedChartsProps> = ({ res, shockData }) => {
  const [chartData, setChartData] = useState<any[]>([]);

  const data = useMemo(() => {
    return shockData?.filter?.((item) => item.type === 1);
  }, [shockData]);

  useEffect(() => {
    if (data.length > 0 && data[0].shock.length !== 0 && data[0].shock[0].list.length !== 0) {
      const datas = data[0].shock[0].list[0];
      setChartData([
        {
          name: `${data[0].shock[0].modelName}-${datas.name}`,
          type: "line",
          stack: "Total",
          data: datas.data,
        },
      ]);
    }
  }, [data]);

  if (res && data && data.length !== 0) {
    return (
      <div className={"supertest-observer"}>
        <ObserverWrapper name='30kmVibrationComfort'>
          {data[0].shock.map((item, inx) => (
            <div key={inx} className='flex items-center justify-center text-[#FFFFFF] text-12 h-32 bg-[#2FA7FF]'>
              {item.modelName}
            </div>
          ))}
          {!!chartData && chartData.length !== 0 && (
            <div
              className='flex justify-center items-center border-1 border-solid border-[#2459FF] my-10'
              style={{ boxShadow: " 0px 4px 20px 0px rgba(50,100,255,0.10)" }}
            >
              <LineChart
                id={"30kmVibrationComfort"}
                width={initSzieForScale(270)}
                height={initSzieForScale(130)}
                dataSource={chartData}
              />
            </div>
          )}
        </ObserverWrapper>
        <FixedTable dataSource={res} title='30km/h震動數據' />
      </div>
    );
  }

  return <></>;
};

// 60km/h 震動曲線表現
const CarShakeLine60: React.FC<ISpeedChartsProps> = ({ res, shockData }) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const data = useMemo(() => {
    return shockData?.filter?.((item) => item.type === 2);
  }, [shockData]);

  useEffect(() => {
    if (data.length > 0 && data[0].shock.length !== 0 && data[0].shock[0].list.length !== 0) {
      const datas = data[0].shock[0].list[0];
      setChartData([
        {
          name: `${data[0].shock[0].modelName}-${datas.name}`,
          type: "line",
          stack: "Total",
          data: datas.data,
        },
      ]);
    }
  }, [data]);

  if (res && data && data.length !== 0) {
    return (
      <div className={"supertest-observer"}>
        <ObserverWrapper name='30kmVibrationComfort'>
          {data[0].shock.map((item, inx) => (
            <div key={inx} className='flex items-center justify-center text-[#FFFFFF] text-12 h-32 bg-[#2FA7FF]'>
              {item.modelName}
            </div>
          ))}
          {!!chartData && chartData.length !== 0 && (
            <div
              className='flex justify-center items-center border-1 border-solid border-[#2459FF] my-10'
              style={{ boxShadow: " 0px 4px 20px 0px rgba(50,100,255,0.10)" }}
            >
              <LineChart
                id={"60kmVibrationComfort"}
                width={initSzieForScale(270)}
                height={initSzieForScale(130)}
                dataSource={chartData}
              />
            </div>
          )}
        </ObserverWrapper>
        <FixedTable title='60km/h震動數據' dataSource={res} />
      </div>
    );
  }

  return <></>;
};

const Soft: React.FC<IProps> = ({ comfortModelYearId }) => {
  const [charts, setCharts] = useState<ChartsType>({});
  const [chartMapData, setChartsMapData] = useState<ChartsType>({});
  const [kindData, setKindData] = useState<KindDataType>();

  const formateMapData = (data: ChartsType) => {
    const obj = {};
    for (let key in data) {
      const mapkey = data[key]?.["templateId"];
      if (mapkey) {
        obj[mapkey] = data[key];
      }
    }

    setChartsMapData(obj);
  };

  const RideData: any = useMemo(() => validateData(chartMapData, 5), [chartMapData]);
  const SoundproofingData = useMemo(() => validateData(chartMapData, 8), [chartMapData]);
  const SoundproofingTitle = useMemo(() => {
    return validateData(chartMapData, 8) ? validateData(chartMapData, 8)!.chartData.map((item: any) => item.title) : [];
  }, [chartMapData]);
  const Speed30KMData = useMemo(() => validateData(chartMapData, 7), [chartMapData]);
  const Speed60KMData = useMemo(() => validateData(chartMapData, 15), [chartMapData]);

  // 舒适实测
  const { data = defaultSoftData } = useQuery<ISoftData>(["fetchSuperTestCharts", comfortModelYearId], async () => {
    if (!comfortModelYearId) return defaultSoftData;
    try {
      const res: { data; status } = await fetchSuperTestCharts({ myid: comfortModelYearId, type: "comfort" });
      if (res?.status === 12000) {
        return res.data;
      }
    } catch (error) {}
    return defaultSoftData;
  });

  const initData: any = {};
  const { data: shockData = initData } = useQuery<any>(
    [`fetchSuperTestShock`, data.myids],
    async () => {
      const res = await fetchSuperTestShock({ myids: data.myids });

      return res?.status === 12000 ? res.data : initData;
    },
    {
      enabled: !!data.myids,
    },
  );

  useEffect(() => {
    setCharts(data.chartData);
    setKindData(data.kindData);
    formateMapData(data.chartData);
  }, [data]);

  return (
    <div className='Soft pt-30'>
      {RideData && (
        <>
          <Title title='視野實測' />
          <div className='border-2 border-solid border-[#EAEFFF] rounded-4 mx-4 pb-4 mb-50'>
            <ViewTestTab charts={charts} dataSource={RideData.chartData} />
          </div>
        </>
      )}

      {SoundproofingData && (
        <>
          <Title title='車室隔音' />
          <div className='border-2 border-solid border-[#EAEFFF] rounded-4 mx-4 pb-4 mb-50'>
            {SoundproofingData.chartName && <TextBox text={SoundproofingData.chartName} />}
            <div className='pt-16 mb-14'>
              {!!formartChartText(SoundproofingData.chartData).length &&
                formartChartText(SoundproofingData.chartData).map((item, inx) => (
                  <div className='flex justify-center items-center' key={inx}>
                    {item.map((ii: string, index: number) => (
                      <div className='flex flex-col flex-auto items-center' key={index}>
                        <span className='text-[#3264FF] text-16'>{ii}</span>
                        <p className='text-[#999999] text-12'>{SoundproofingTitle[index]}</p>
                      </div>
                    ))}
                  </div>
                ))}
            </div>

            {kindData && <CarSoundproofingDom res={SoundproofingData} kindData={kindData} />}
          </div>
        </>
      )}

      {Speed30KMData && shockData?.length > 0 && (
        <>
          <Title title='底盤吸震' />
          <div className='border-2 border-solid border-[#EAEFFF] rounded-4 mx-4 pb-4 mb-30'>
            <TextBox text={"30km/h震動曲線表現"} />
            <CarShakeLine30 res={Speed30KMData} shockData={shockData} />
          </div>
        </>
      )}

      {Speed60KMData && shockData?.length > 0 && (
        <div className='border-2 border-solid border-[#EAEFFF] rounded-4 mx-4 pb-4 mb-50'>
          <TextBox text={"60km/h震動曲線表現"} />
          <CarShakeLine60 res={Speed60KMData} shockData={shockData} />
        </div>
      )}
    </div>
  );
};

export default Soft;
